import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SEOHead from "../../../../components/SEO/SEOHead";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import { useRTDBGames } from "../rtdb/hooks";
import { getCurrentNHLSeason, formatNHLSeason } from "../utils/nhlSeasonDetector";
import { isGameFinal, isGameLive } from "../rtdb/rtdbConfig";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import NHLDesktopGameCard from "../components/NHLDesktopGameCard";
import BetaNotice from "../components/BetaNotice";
import StatsPanel from "../components/StatsPanel";
import GamesSection from "../components/GamesSection";
import DesktopPredictionBar from "../components/DesktopPredictionBar";
import NHLOddsModal from "../NHLOddsModal";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { NHL_HEADER_COPY } from "../constants/headerContent";
import DesktopRailNavigation from "../../NFL/components/desktop/DesktopRailNavigation";
import { StatusModal } from "../../NFL/components/desktop/modals";
import useNHLRailFilters from "../../../../components/dashboards/mobile/hooks/useNHLRailFilters";
import { DatePickerModal } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { nhlDashPtBR } from "../../../../components/mappings/sports-dash-mappings/nhl-dash.pt-br";

const DEFAULT_PAGINATION = {
  currentPage: 1,
  perPage: 16,
  totalPages: 1
};

const NHLDesktopDashboard = () => {
  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? nhlDashPtBR[text] || text : text),
    [isBrazilian]
  );

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const cacheCheckKeyRef = useRef(null);

  const seasonInfo = useMemo(() => getCurrentNHLSeason(), []);
  const seasonLabel = formatNHLSeason(seasonInfo);

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    if (nextFilters.date) {
      setSelectedDate(nextFilters.date);
    }
  }, []);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet: showDateModal,
    setShowDateSheet: setShowDateModal,
    showStatusSheet: showStatusModal,
    setShowStatusSheet: setShowStatusModal
  } = useNHLRailFilters({
    initialDate: selectedDate,
    onFiltersChange: handleRailFiltersChange
  });

  const {
    games,
    loading: gamesLoading,
    error: gamesError,
    refetch: refetchGames
  } = useRTDBGames({
    date: selectedDate,
    season: seasonInfo.season,
    teamId: null,
    enabled: true,
    realtime: true
  });

  const {
    isLoading: isPredicting,
    usageState,
    error: predictionError,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    isGameFrozen,
    selectionCount,
    selectedGames: selectedGamesForPrediction,
    checkCachedPredictions
  } = useQuickPrediction();

  // Filter games based on rail filters
  const filteredGames = useMemo(() => {
    let filtered = [...games];

    // Filter by status
    if (railFilters.status === 'live') {
      filtered = filtered.filter((game) => isGameLive(game?.status?.short));
    } else if (railFilters.status === 'upcoming') {
      filtered = filtered.filter(
        (game) => !isGameLive(game?.status?.short) && !isGameFinal(game?.status?.short)
      );
    } else if (railFilters.status === 'finished') {
      filtered = filtered.filter((game) => isGameFinal(game?.status?.short));
    }

    // Filter by conference/division (if teams have this data)
    if (railFilters.conference !== 'all' && railFilters.division === 'all') {
      // Conference filter only
      filtered = filtered.filter((game) => {
        const homeConf = game?.teams?.home?.conference?.toLowerCase();
        const awayConf = game?.teams?.away?.conference?.toLowerCase();
        return homeConf === railFilters.conference || awayConf === railFilters.conference;
      });
    } else if (railFilters.division !== 'all') {
      // Division filter
      filtered = filtered.filter((game) => {
        const homeDiv = game?.teams?.home?.division?.toLowerCase();
        const awayDiv = game?.teams?.away?.division?.toLowerCase();
        return homeDiv === railFilters.division || awayDiv === railFilters.division;
      });
    }

    return filtered;
  }, [games, railFilters]);

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      totalPages: Math.max(1, Math.ceil((filteredGames?.length || 0) / prev.perPage))
    }));
  }, [filteredGames]);

  const liveGames = useMemo(
    () => games.filter((game) => isGameLive(game?.status?.short)),
    [games]
  );

  const finishedGames = useMemo(
    () => games.filter((game) => isGameFinal(game?.status?.short)),
    [games]
  );

  const upcomingGames = useMemo(
    () => games.filter(
      (game) => !isGameLive(game?.status?.short) && !isGameFinal(game?.status?.short)
    ),
    [games]
  );

  const stats = useMemo(
    () => ({
      total: filteredGames.length,
      live: liveGames.length,
      upcoming: upcomingGames.length,
      finished: finishedGames.length
    }),
    [filteredGames.length, liveGames.length, upcomingGames.length, finishedGames.length]
  );

  // Prime predictions from cache/Firestore when games change
  useEffect(() => {
    if (!games.length) return;
    const gameIds = games.map((g) => g.id || g.gameId).filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;
    checkCachedPredictions?.("nhl", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (NHL desktop):", err)
    );
  }, [games, checkCachedPredictions]);

  const handlePageChange = useCallback((page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: Math.min(Math.max(page, 1), prev.totalPages)
    }));
  }, []);

  const renderGameCard = useCallback(
    (game, index) => {
      const id = game.id || `game-${index}`;
      const gameWithId = { ...game, id };

      return (
        <NHLDesktopGameCard
          key={gameWithId.id}
          game={gameWithId}
          isSelected={isGameSelected(gameWithId)}
          isFrozen={isGameFrozen(gameWithId.id)}
          onToggleSelection={() => toggleGameSelection(gameWithId)}
          prediction={getGamePrediction(gameWithId.id)}
          onViewOdds={setSelectedGameForOdds}
        />
      );
    },
    [getGamePrediction, isGameFrozen, isGameSelected, toggleGameSelection]
  );

  // Auto-reset selections when a prediction request finishes
  useEffect(() => {
    if (isPredicting) return;

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      activePredictionRequestRef.current = null;
      setTimeout(() => clearSelections(), 600);
    }
  }, [clearSelections, isPredicting, predictionFlowActive, predictionRequestId]);

  const handlePredictRequest = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("nhl");
    clearSelections(); // hide bar and reset counts immediately
    setPredictionFlowActive(false);
  }, [clearSelections, predictSelectedGames]);

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <SEOHead
        title="NHL Dashboard - Live Games & AI Picks"
        description="Track NHL games, monitor live scores, and request AI predictions."
        keywords="nhl, hockey, dashboard, live scores"
      />

      <UnifiedNavHeader title="NHL Dashboard" showBackButton />

      <div className="relative px-6 py-8">
        <div className="max-w-[1600px] mx-auto space-y-4">
          <Header t={t} {...NHL_HEADER_COPY} />
        </div>
        <div className="max-w-[1600px] mx-auto space-y-4 mt-4">
          {/* <BetaNotice /> */}

          {/* Desktop Rail Navigation - Filter Rail */}
          <DesktopRailNavigation
            sport="nhl"
            primaryTabs={railPrimaryTabs}
            filters={railFilterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={resetRailFilters}
            hasActiveFilters={railHasActiveFilters}
          />

          <StatsPanel stats={stats} />
          <GamesSection
            games={filteredGames}
            loading={gamesLoading}
            error={gamesError}
            pagination={pagination}
            onPageChange={handlePageChange}
            renderGame={renderGameCard}
            dateLabel={selectedDate}
            onRetry={refetchGames}
          />
        </div>
      </div>

      {selectedGameForOdds && (
        <NHLOddsModal
          game={selectedGameForOdds}
          isOpen={!!selectedGameForOdds}
          onClose={() => setSelectedGameForOdds(null)}
        />
      )}

      {/* Desktop Filter Modals */}
      <DatePickerModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        selectedDate={ensureISODate(railFilters.date || selectedDate)}
        onDateSelect={(isoDate) => {
          const formatted = ensureISODate(isoDate, selectedDate);
          updateRailFilter("date", formatted);
          setSelectedDate(formatted);
          setShowDateModal(false);
        }}
      />

      <StatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={railFilters.status || 'all'}
        onStatusChange={(status) => {
          updateRailFilter('status', status);
          setShowStatusModal(false);
        }}
        sport="nhl"
      />

      <DesktopPredictionBar
        selectionCount={selectionCount}
        usageState={usageState}
        isLoading={isPredicting}
        error={predictionError}
        onPredict={handlePredictRequest}
        onClear={clearSelections}
        selectedGames={selectedGamesForPrediction || []}
      />
    </div>
  );
};

export default NHLDesktopDashboard;
